import { Injectable } from "@angular/core";
import { CustomerApiService, StudentApiService } from "../api";
import { ExpectedCompanyResponseDto, ProgressItemResponse, ProgressStatus, SituationMemo, Student, StudentSituationMemo, User, Yomi3ProbabilityCalcResult, YomiType } from "@agent-ds/shared/interfaces";
import { Observable } from "rxjs";
import { AmountRows, MonthlySalesOverview, SalesAmount, SalesAmountForUser, SalesSummary, SalesSummaryForUser, SalesSummaryRow, TeamMonthlySalesOverviews } from "@agent-ds/shared/interfaces/sale";
import * as moment from "moment";
import { cleanObject } from "@agent-ds/shared/util/util";
import * as _ from "underscore";

// progress-statuses.ts の SEMINAR_TYPE は progress-actions.ts を経由して循環依存が生じるため、ここでローカル定義する
const SEMINAR_TYPE = {
  SEMI_FINAL_INTERVIEW: 99,
  FINAL_INTERVIEW: 100,
} as const;

export interface YomiFormatedStudentSituationMemoData {
  memo: string;
  orderOfPreference: string;
  nextAction: string;
}

export interface YomiFormatedStudentSituationMemo extends StudentSituationMemo {
  situationMemoData?: YomiFormatedStudentSituationMemoData
}

export enum Yomi3Target {
  schedule = 'yomi3Schedule',
  passed = 'yomi3Passed',
  inclination = 'yomi3Inclination',
}

export enum ExpectedCompanyTarget {
  Company1 = 'p_expected_company_flag',
  Company2 = 'p_expected_company2_flag',
}

@Injectable({
  providedIn: 'root',
})
export class CustomerExpectedCompanyYomiService {
  /**
   * コンストラクタ
   */
  constructor(
    private customerApiService: CustomerApiService,
    private studentService: StudentApiService,
  ) {}

  private getSalesTargetCustomerListSize(isManager = false): number {
    return isManager ? 500 : 200;
  }

  public getDefaultSortedCustomerProgressess(customersProgresses: ProgressItemResponse[]): ProgressItemResponse[] {
    customersProgresses.sort((aP, bP) => {
      const a = aP.student;
      const b = bP.student;
      if (!b.dynamicData.rank || a.dynamicData.rank < b.dynamicData.rank) {
        return -1;
      }
      if (a.dynamicData.rank > b.dynamicData.rank) {
        return 1;
      }
      return 0;
    });
    return customersProgresses;
  }

  public getDefaultSortedCustomers(customers: Student[]): Student[] {
    customers.sort((a, b) => {
      if (!b.dynamicData.rank || a.dynamicData.rank < b.dynamicData.rank) {
        return -1;
      }
      if (a.dynamicData.rank > b.dynamicData.rank) {
        return 1;
      }
      return 0;
    });
    return customers;
  }

  public pickTop1StudentSituationMemoWithSortAndFormat(studentSituationMemos?: StudentSituationMemo[]): StudentSituationMemo {
    if (studentSituationMemos && studentSituationMemos.length >= 1) {
      studentSituationMemos.sort((a, b) => {
        if (a.registerDate > b.registerDate) {
          return -1;
        }
        if (a.registerDate < b.registerDate) {
          return 1;
        }
        return (b.id || 0) - (a.id || 0);
      });
      const situationMemo = studentSituationMemos[0];
      situationMemo.situationMemoData = {
        memo: this.handlerSituationMemoEtcView(situationMemo),
        orderOfPreference: this.handlerSituationMemoWishView(situationMemo),
        nextAction: this.handlerSituationMemoNextView(situationMemo)
      };
      return situationMemo;
    } else {
      const situationMemo : StudentSituationMemo = {};
      situationMemo.situationMemoData = {};
      return situationMemo;
    }
  }

  handlerSituationMemoWishView(picMemo): string {
    if (!picMemo) {
      return '';
    }

    const memo = picMemo.situationMemo;

    const wishMemo = memo.indexOf('志望状況');
    const nextMemo = memo.indexOf('ネクストアクション');

    if (wishMemo !== -1 && nextMemo !== -1) {
      const cut = memo.slice(wishMemo, nextMemo - 3);
      return cut.replace('志望状況', '').trim();
    }

    return '';
  }

  handlerSituationMemoNextView(picMemo): string {
    if (!picMemo) {
      return '';
    }

    const memo = picMemo.situationMemo;

    const nextMemo = memo.indexOf('ネクストアクション');
    const etcMemo = memo.indexOf('その他メモ');

    if (nextMemo !== -1 && etcMemo !== -1) {
      const cut = memo.slice(nextMemo, etcMemo - 3);
      return cut.replace('ネクストアクション', '').trim();
    }

    return '';
  }

  handlerSituationMemoEtcView(picMemo): string {
    if (!picMemo) {
      return '';
    }

    const memo = picMemo.situationMemo;

    const etcMemo = memo.indexOf('その他メモ');

    if (etcMemo !== -1) {
      const cut = memo.slice(etcMemo);
      return cut.replace('その他メモ', '').trim();
    }

    return '';
  }

  public makeCustomersExpectedCompanyUIList(expectedCompanies: ExpectedCompanyResponseDto[]): {label:string, value:number}[] {
    const noExpectedCompanyDefaultItem = { label: '-----', value: 0 };
    return [
        noExpectedCompanyDefaultItem,
        ...expectedCompanies.map(d => ({
          label: d.e_name,
          value: d.p_id
        }))
    ];
  }

  public fetchMySalesReport(selectedExpectedContractMonthValue: string) {
    if(!this.validMonthValue(selectedExpectedContractMonthValue)) {
      return;
    }
    const expectedContractMonth = moment(selectedExpectedContractMonthValue, 'YYYY-MM');
    const params = {
      contractDateFrom: expectedContractMonth
          .clone()
          .startOf('month')
          .format('YYYY-MM-DD'),
      contractDateTo: expectedContractMonth
          .clone()
          .endOf('month')
          .format('YYYY-MM-DD'),
    };

    return this.customerApiService.getSalesReportOfCurrentUser(params);
  }

  fetchMyMonthlySalesOverview(selectedExpectedContractMonthValue: string) {
    if(!this.validMonthValue(selectedExpectedContractMonthValue)) {
      return;
    }
    const selectedMonth = moment(selectedExpectedContractMonthValue, 'YYYY-MM');
    const year = selectedMonth.clone().startOf('month').format('YYYY');
    const month = selectedMonth.clone().startOf('month').format('MM');

    return this.customerApiService.getMyMonthlySalesOverview(year, month, {});
  }

  fetchTeamSalesReport(teamId: number, selectedExpectedContractMonthValue: string) {
    if(!this.validMonthValue(selectedExpectedContractMonthValue)) {
      return;
    }
    const expectedContractMonth = moment(selectedExpectedContractMonthValue, 'YYYY-MM');
    const params = {
      contractDateFrom: expectedContractMonth
          .clone()
          .startOf('month')
          .format('YYYY-MM-DD'),
      contractDateTo: expectedContractMonth
          .clone()
          .endOf('month')
          .format('YYYY-MM-DD'),
      teamId: [teamId]
    };

    return this.customerApiService.getSalesReport(params);
  }

  fetchTeamMonthlySalesOverviews(teamId: number, selectedExpectedContractMonthValue: string) {
    if(!this.validMonthValue(selectedExpectedContractMonthValue)) {
      return;
    }
    const selectedMonth = moment(selectedExpectedContractMonthValue, 'YYYY-MM');
    const year = selectedMonth.clone().startOf('month').format('YYYY');
    const month = selectedMonth.clone().startOf('month').format('MM');

    return this.customerApiService.getTeamMonthlySalesOverviews(teamId, year, month, {});
  }

  fetchMySalesTargetCustomerList(selectedExpectedContractMonthValue: string, extraParams?: Record<string, any>) {
    if(!this.validMonthValue(selectedExpectedContractMonthValue)) {
      return;
    }
    const params = cleanObject({
      from: 0,
      size: 4000,
      sort: 'registrationStatus',
      order: 'desc',
      ...extraParams,
    });

    return this.customerApiService
      .getMySalesTargetCustomerListOfExpectedContractMonth(
        selectedExpectedContractMonthValue,
        params
      );
  }

  fetchTeamSalesTargetCustomerList(teamId: number, selectedExpectedContractMonthValue: string, extraParams?: Record<string, any>) {
    if(!this.validMonthValue(selectedExpectedContractMonthValue)) {
      return;
    }
    const params = cleanObject({
      from: 0,
      size: 4000,
      sort: 'registrationStatus',
      order: 'desc',
      ...extraParams,
    });

    return this.customerApiService
      .getTeamSalesTargetCustomerListOfExpectedContractMonth(
        teamId,
        selectedExpectedContractMonthValue,
        params
      );
  }

  fetchMyRaSalesTargetCustomerList(
    selectedExpectedContractMonthValue: string,
    page = 1,
    pageSize = 50,
    filterParams: Record<string, any> = {},
  ) {
    if(!this.validMonthValue(selectedExpectedContractMonthValue)) {
      return;
    }
    const params = cleanObject({
      from: (page - 1) * pageSize,
      size: pageSize,
      sort: 'status',
      order: 'desc',
      ...filterParams,
    });

    return this.customerApiService
      .getMyRaSalesTargetCustomerListOfExpectedContractMonth(
        selectedExpectedContractMonthValue,
        params
      );
  }

  fetchMyRaSalesTargetCustomerTotal(
    selectedExpectedContractMonthValue: string,
    filterParams: Record<string, any> = {},
  ) {
    if(!this.validMonthValue(selectedExpectedContractMonthValue)) {
      return;
    }
    const params = cleanObject({
      sort: 'status',
      order: 'desc',
      ...filterParams,
    });

    return this.customerApiService
      .getMyRaSalesTargetCustomerTotalOfExpectedContractMonth(
        selectedExpectedContractMonthValue,
        params
      );
  }

  fetchTeamRaSalesTargetCustomerList(
    teamId: number,
    selectedExpectedContractMonthValue: string,
    page = 1,
    pageSize = 50,
    filterParams: Record<string, any> = {},
  ) {
    if(!this.validMonthValue(selectedExpectedContractMonthValue)) {
      return;
    }
    const params = cleanObject({
      from: (page - 1) * pageSize,
      size: pageSize,
      sort: 'status',
      order: 'desc',
      ...filterParams,
    });

    return this.customerApiService
      .getTeamRaSalesTargetCustomerListOfExpectedContractMonth(
        teamId,
        selectedExpectedContractMonthValue,
        params
      );
  }

  fetchTeamRaSalesTargetCustomerTotal(
    teamId: number,
    selectedExpectedContractMonthValue: string,
    filterParams: Record<string, any> = {},
  ) {
    if(!this.validMonthValue(selectedExpectedContractMonthValue)) {
      return;
    }
    const params = cleanObject({
      sort: 'status',
      order: 'desc',
      ...filterParams,
    });

    return this.customerApiService
      .getTeamRaSalesTargetCustomerTotalOfExpectedContractMonth(
        teamId,
        selectedExpectedContractMonthValue,
        params
      );
  }

  isCurrentMonth5WeekTarget() {
    const startOfMonth = moment().startOf('month');
    const endOfMonth = moment().endOf('month');
    const weeksInMonth = endOfMonth.diff(startOfMonth, 'weeks') + 1;
    return weeksInMonth === 5;
  }

  currentWeekTarget() {
    const startOfMonth = moment().startOf('month');
    return moment().diff(startOfMonth, 'weeks') + 1;
  }

  handleSaveMyMonthlySalesOverviewForTargetMonth(
    selectedExpectedContractMonthValue: string,
    salesSummaryForUserModel: SalesSummaryForUser,
    salesType: 'ca' | 'rapa' = 'ca',
  ): Observable<any> {
    if(!this.validMonthValue(selectedExpectedContractMonthValue)) {
      return;
    }
    const year = moment(selectedExpectedContractMonthValue).startOf('month').format('YYYY');
    const month = moment(selectedExpectedContractMonthValue).startOf('month').format('MM');
    const summaryRow = (salesSummaryForUserModel && salesSummaryForUserModel[salesType]) || {};
    return this.customerApiService.updateMyMonthlySalesOverview(year, month, {
      targetGk: summaryRow.gkTarget && Number(summaryRow.gkTarget),
      targetSn: summaryRow.snTarget && Number(summaryRow.snTarget),
      externalRevenue: summaryRow.externalSalesRevenue && Number(summaryRow.externalSalesRevenue),
    });
  }

  handleSaveMemo(studentId:number, memo: StudentSituationMemo, loginUser: User): Observable<any> {
    const memoData: YomiFormatedStudentSituationMemoData = memo.situationMemoData as any;
    const memoString = `◾️志望状況\n\n${memoData.orderOfPreference || ''}\n\n◾️ネクストアクション\n\n${memoData.nextAction || ''}\n\n◾️その他メモ\n\n${memoData.memo || ''}`;
    const newMemo: SituationMemo = {
      registerDate: moment().format('YYYY-MM-DD'),
      situationMemo: memoString,
      userId: loginUser.id,
    };

    return this.studentService.addMemo(studentId, newMemo);
  }

  handleSaveMgrMemo(studentId: number, memoString: string): Observable<any> {
    return this.studentService.updateMgrMemo({
      studentId,
      memoString,
    });
  }

  handleSaveRaMemo(progressId:number, memoString: string): Observable<any> {
    return this.customerApiService.updateRaMemo({
      progressId,
      memoString
    });
  }

  public handleExpectedSKUpdate(studentId: number, progressId: number|null, expectedSk: number): Observable<any> {
    if(!progressId) {
      return;
    }

    return this.customerApiService
      .updateExpectedSK(
        Number(studentId),
        Number(progressId),
        expectedSk
      );
  }

  public getYearMonthFromDateString(dateString: string) {
    if(!dateString) {
      return '';
    }
    if(!moment(dateString, moment.ISO_8601, true).isValid()) {
      return '';
    }
    return moment(dateString).format('YYYY-MM');
  }
  public handleUpdateExpectedContractMonth(expectedContractMonth: string, student: Student): Observable<any> {
    const formattedExpectedContractMonth = this.formatExpectedContractMonth(expectedContractMonth);
    student.dynamicData.expectedContractMonth = formattedExpectedContractMonth;
    
    return this.customerApiService.updateExpectedContractMonth(
      student.id,
      formattedExpectedContractMonth
    );
  }

  public handleUpdateRaExpectedContractMonth(expectedContractMonth: string, progress: ProgressItemResponse): Observable<any> {
    const formattedExpectedContractMonth = this.formatExpectedContractMonth(expectedContractMonth);
    progress.dynamicData.raExpectedContractMonth = formattedExpectedContractMonth;
    
    return this.customerApiService.updateRaExpectedContractMonth(
      progress.id,
      formattedExpectedContractMonth
    );
  }

  public updateCustomerExpectedCompany(studentId: number, newExpectedCompany: number, customersExpectedCompanyListMapping: Record<string, ExpectedCompanyResponseDto[]>): Observable<any> {
    const foundExpectedCompanyByTarget = customersExpectedCompanyListMapping[studentId].find(
      ec => Number(ec.p_expected_company_flag) === 1
    );
    const currentProgressId = foundExpectedCompanyByTarget ? foundExpectedCompanyByTarget.p_id : 0;

    return this.customerApiService.updateExpectedCompanyFlag(studentId, currentProgressId, newExpectedCompany);
  }

  public updateCustomerExpectedCompany2(studentId: number, newExpectedCompany: number, customersExpectedCompanyListMapping: Record<string, ExpectedCompanyResponseDto[]>): Observable<any> {
    const foundExpectedCompanyByTarget = customersExpectedCompanyListMapping[studentId].find(
      ec => Number(ec.p_expected_company2_flag) === 1
    );
    const currentProgressId = foundExpectedCompanyByTarget ? foundExpectedCompanyByTarget.p_id : 0;

    return this.customerApiService.updateExpectedCompany2Flag(studentId, currentProgressId, newExpectedCompany);
  }

  public makeYomi3ProbabilityDataFrom(expectedCompanyData: ExpectedCompanyResponseDto): Yomi3ProbabilityCalcResult {
    return {
      label: expectedCompanyData.p_yomi_3_probability_label,
      probability: expectedCompanyData.p_yomi_3_probability,
    };
  }

  public makeRaYomi3ProbabilityDataFrom(progress: ProgressItemResponse): Yomi3ProbabilityCalcResult {
    return {
      label: progress.dynamicData.raYomi3ProbabilityResultLabel,
      probability: progress.dynamicData.raYomi3ProbabilityResult,
    };
  }

  public callCalculateApiForYomi3Probability(expectedCompanyData: ExpectedCompanyResponseDto): Observable<Yomi3ProbabilityCalcResult> {
    return this.customerApiService.calcYomi3Probability({
      yomi3Passed:expectedCompanyData.p_yomi_3_passed,
      yomi3Inclination:expectedCompanyData.p_yomi_3_inclination,
      yomi3Schedule:expectedCompanyData.p_yomi_3_schedule,
    });
  }

  public callCalculateApiForRaYomi3Probability(progress: ProgressItemResponse): Observable<Yomi3ProbabilityCalcResult> {
    return this.customerApiService.calcYomi3Probability({
      yomi3Passed: progress.dynamicData.raYomi3Passed,
      yomi3Inclination: progress.dynamicData.raYomi3Inclination,
      yomi3Schedule: progress.dynamicData.raYomi3Schedule,
    });
  }

  public handlerYomiTypeChange(studentId: number, customers: Student[]): Observable<any> {
    const studentData: Student = customers.find(customer => customer.id === studentId);
    return this.customerApiService.updateYomi(studentId, { yomi: studentData.dynamicData.yomi });
  }

  public handlerRaYomiTypeChange(progressId: number, raYomi: YomiType): Observable<any> {
    return this.customerApiService.updateRaYomi(progressId, { yomi: raYomi });
  }

  public handleYomi3Change({
    expectedCompany,
    studentId,
    yomi3Target
  } : {
    expectedCompany: ExpectedCompanyResponseDto,
    studentId: number,
    yomi3Target: Yomi3Target
  }): Observable<any> {
    if(!expectedCompany || !expectedCompany.p_id) {
      return;
    }
    const yomi3TargetData = {
      [Yomi3Target.passed]: expectedCompany.p_yomi_3_passed,
      [Yomi3Target.inclination]: expectedCompany.p_yomi_3_inclination,
      [Yomi3Target.schedule]: expectedCompany.p_yomi_3_schedule,
    };
    const requestParam = {
      studentId,
      progressId: expectedCompany.p_id,
      yomi3Value: yomi3TargetData[yomi3Target],
    };
    switch(yomi3Target) {
      case Yomi3Target.passed:
        return this.customerApiService.updateYomi3Passed(requestParam);
      case Yomi3Target.inclination:
        return this.customerApiService.updateYomi3Inclination(requestParam);
      case Yomi3Target.schedule:
        return this.customerApiService.updateYomi3Schedule(requestParam);
    }
  }

  public handleRaYomi3Change({
    progress,
    yomi3Target
  } : {
    progress: ProgressItemResponse,
    yomi3Target: Yomi3Target
  }): Observable<any> {
    if(!progress || !progress.id) {
      return;
    }
    const yomi3TargetData = {
      [Yomi3Target.passed]: progress.dynamicData.raYomi3Passed,
      [Yomi3Target.inclination]: progress.dynamicData.raYomi3Inclination,
      [Yomi3Target.schedule]: progress.dynamicData.raYomi3Schedule,
    };
    const requestParam = {
      studentId: progress.studentId,
      progressId: progress.id,
      yomi3Value: yomi3TargetData[yomi3Target],
    };
    switch(yomi3Target) {
      case Yomi3Target.passed:
        return this.customerApiService.updateRaYomi3Passed(requestParam);
      case Yomi3Target.inclination:
        return this.customerApiService.updateRaYomi3Inclination(requestParam);
      case Yomi3Target.schedule:
        return this.customerApiService.updateRaYomi3Schedule(requestParam);
    }
  }

  public getEnterpriseNameFromProgress(progress: ProgressItemResponse): string {
    if (!progress) {
      return '';
    }
    const progressDynamicData = progress.dynamicData || {};
    const progressJob = (progress as any).job || {};
    const enterpriseDepartment = progressJob.enterpriseDepartment || {};
    const jobEnterprise = enterpriseDepartment.enterprise || {};
    const jobEnterpriseDynamicData = jobEnterprise.dynamicData || {};
    const progressEnterprise = (progress as any).enterprise || {};

    return jobEnterpriseDynamicData.name || jobEnterprise.name || progressEnterprise.name || progressDynamicData.enterpriseName || '';
  }

  public handlerExpectedCompanyTypeView(type: number): string {
    if (type === 0) {
      return 'AG';
    }

    if (type === 1) {
      return 'PLUS';
    }

    return '';
  }
  public handlerProgressStatusView(
    expectedCompany: ExpectedCompanyResponseDto,
    PROGRESS_STATUSES: {
      [key: number]: ProgressStatus;
    }
  ): string {
    let word = '';

    if (!expectedCompany.p_status || !(expectedCompany.p_status in PROGRESS_STATUSES)) {
      return word;
    }

    if (expectedCompany.p_seminar_type === SEMINAR_TYPE.SEMI_FINAL_INTERVIEW) {
      word = '最終前';
    } else if (expectedCompany.p_seminar_type === SEMINAR_TYPE.FINAL_INTERVIEW) {
      word = '最終';
    } else if (expectedCompany.p_n) {
      word = `${expectedCompany.p_n}次`;
    }

    word = word + PROGRESS_STATUSES[expectedCompany.p_status].label;

    return word;
  }
  public getProgressStatusViewFromProgress(
    progress: ProgressItemResponse,
    PROGRESS_STATUSES: {
      [key: number]: ProgressStatus;
    }
  ): string {
    let word = '';

    if (!progress.status || !(progress.status in PROGRESS_STATUSES)) {
      return word;
    }

    if (progress.seminarType === SEMINAR_TYPE.SEMI_FINAL_INTERVIEW) {
      word = '最終前';
    } else if (progress.seminarType === SEMINAR_TYPE.FINAL_INTERVIEW) {
      word = '最終';
    } else if (progress.n) {
      word = `${progress.n}次`;
    }

    word = word + PROGRESS_STATUSES[progress.status].label;

    return word;
  }
  public handlerGraduateYearView(year): string {
    if (!isNaN(year)) {
      const newYear = String(year);
      return newYear.slice(2);
    }
    return year.slice(2);
  }
  public handlerDateMonthDayPlusjp(dateData) {
    const newMonthDay = dateData.slice(5, 10);

    return newMonthDay.replace('-', '/');
  }
  public openChat(lineId): void {
    if (!lineId) {
      return;
    }
    const url = `https://web.onetoone.console.mico-cloud.jp/ja/dRSUyB8vQqmklRCC2t_85A/talks/${lineId}`;
    window.open(url, '_blank');
  }

  public doCalculateSalesSummaryForTeam(
    salesAmount: SalesAmount,
    teamMonthlySalesOverviews: TeamMonthlySalesOverviews
  ): SalesSummary {
    // CA
    const caUserSummaries: { [userId: string]: SalesSummaryRow } = {};
    ((salesAmount && salesAmount.ca && salesAmount.ca.users) || []).forEach(
      salesAmountRow => {
        const userId = salesAmountRow.userId;
        const salesOverViewOfTargetMonth =
          teamMonthlySalesOverviews &&
          teamMonthlySalesOverviews.users &&
          teamMonthlySalesOverviews.users[userId];
        caUserSummaries[userId] = this.makeSalesSummaryRow(
          salesAmountRow,
          salesOverViewOfTargetMonth || null,
        );
      }
    );
    const caTotalSummary = this.makeSalesSummaryRow(
      (salesAmount && salesAmount.ca && salesAmount.ca.total) || null,
      (teamMonthlySalesOverviews && teamMonthlySalesOverviews.total) || null,
    );
    const rapaUserSummaries: { [userId: string]: SalesSummaryRow } = {};
    ((salesAmount && salesAmount.rapa && salesAmount.rapa.users) || []).forEach(
      salesAmountRow => {
        const userId = salesAmountRow.userId;
        const salesOverViewOfTargetMonth =
          teamMonthlySalesOverviews &&
          teamMonthlySalesOverviews.users &&
          teamMonthlySalesOverviews.users[userId];
        rapaUserSummaries[userId] = this.makeSalesSummaryRow(
          salesAmountRow,
          salesOverViewOfTargetMonth || null,
        );
      }
    );
    const rapaTotalSummary = this.makeSalesSummaryRow(
      (salesAmount && salesAmount.rapa && salesAmount.rapa.total) || null,
      (teamMonthlySalesOverviews && teamMonthlySalesOverviews.total) || null,
    );

    // 返却オブジェクトを構築
    return {
      ca: { users: caUserSummaries, total: caTotalSummary },
      rapa: { users: rapaUserSummaries, total: rapaTotalSummary },
    };
  }

  public doCalculateSalesSummaryForUser(
    salesAmountForUser: SalesAmountForUser,
    monthlySalesOverview: MonthlySalesOverview,
  ): SalesSummaryForUser {

    const { ca: caSalesAmountRow = null, rapa: rapaSalesAmountRow = null } = (salesAmountForUser || {});
    // CA行のサマリーを作成（他区分が増える場合は同様に呼び出し）
    const caSummary = this.makeSalesSummaryRow(caSalesAmountRow, monthlySalesOverview);
    const rapaSummary = this.makeSalesSummaryRow(rapaSalesAmountRow, monthlySalesOverview);

    // 返却オブジェクトを構築
    return {
      ca: caSummary,
      rapa: rapaSummary,
    };
  }

  private makeSalesSummaryRow(
    salseAmountRow: AmountRows | null,
    monthlySalesOverview: MonthlySalesOverview | null,
  ): SalesSummaryRow {
    // 月次サマリ側：プロパティごとにデフォルト値を付ける
    const {
      targetSn = null,
      targetGk = null,
      externalRevenue = null, // 0 は有効値。未設定は null/undefined として扱う
    } = monthlySalesOverview || {};

    // 売上金額側：こちらも同様にデフォルト値を付ける
    const {
      snFCST = null,
      skFCST = null,
    } = salseAmountRow || {}; // 0 もあり得るのでそのまま

    const safe = (v: number | null | undefined) => (v == null ? 0 : v);

    // SN目標達成率 = SN FCST / SN目標
    const snTargetAchievementRate =
      targetSn != null ? ((safe(snFCST) / targetSn) * 100) : null;

    // SK目標達成率 = SK FCST / GK目標
    const skTargetAchievementRate =
      targetGk != null ? ((safe(skFCST) / targetGk) * 100) : null;

    // SK残 = SK FCST - GK目標
    const skGapWithTarget =
      (skFCST == null || targetGk == null) ? null : (skFCST - targetGk);

    // GK実績 = SK FCST + 外部売上
    const gkActual =
      (skFCST == null || targetGk == null)
        ? null
        : safe(skFCST) + safe(externalRevenue);

    // GK実績達成率 = GK実績 / GK目標
    const gkActualAchievementRate =
      (targetGk != null && gkActual != null)
        ? ((gkActual / targetGk) * 100)
        : null;

    // サマリー行を返却（null セーフな値を利用）
    return {
      snTarget: targetSn,
      snTargetAchievementRate,
      gkTarget: targetGk,
      skTargetAchievementRate,
      skGapWithTarget,
      externalSalesRevenue: externalRevenue,
      gkActual,
      gkActualAchievementRate,
    };
  }
  private validMonthValue(monthValue: string): boolean {
    if(!/^\d{4}-\d{2}$/.test(monthValue)) {
      return false;
    }
    return true;
  }

  private formatExpectedContractMonth(expectedContractMonth: string): string | null {
    if (!expectedContractMonth) {
      return null;
    }

    if (!this.validMonthValue(expectedContractMonth)) {
      return null;
    }

    return moment(`${expectedContractMonth}-01`).format('YYYY-MM-DD');
  }

}
